import type { GameConfig, GameState, PieceTypeId, ShopOffer } from '../types';
import { getPieceType } from './Pieces';

// shop の駒プール: 障害物以外の購入可能な駒種
function purchasableTypes(config: GameConfig) {
  return config.pieceTypes.filter((t) => t.id !== 'obstacle');
}

// プリセット適用後のプール: loadout が空ならすべて、指定があれば指定駒種のみ
function effectivePool(config: GameConfig, loadoutPreset?: PieceTypeId[]) {
  const all = purchasableTypes(config);
  if (!loadoutPreset || loadoutPreset.length === 0) return all;
  const set = new Set(loadoutPreset);
  const filtered = all.filter((t) => set.has(t.id));
  // フィルタ結果が空なら全駒(防御的フォールバック)
  return filtered.length > 0 ? filtered : all;
}

// 4 枚ランダム提示(重複あり、loadoutPreset があれば pool を絞る)
export function generateShopOffers(config: GameConfig, loadoutPreset?: PieceTypeId[]): ShopOffer[] {
  const pool = effectivePool(config, loadoutPreset);
  const offers: ShopOffer[] = [];
  for (let i = 0; i < config.shop.offerCount; i++) {
    const pick = pool[Math.floor(Math.random() * pool.length)];
    offers.push({ pieceTypeId: pick.id, consumed: false });
  }
  return offers;
}

export function canAffordOffer(state: GameState, offerIndex: number): boolean {
  const offer = state.shop.offers[offerIndex];
  if (!offer || offer.consumed) return false;
  const type = getPieceType(state.config, offer.pieceTypeId);
  return state.player.gold >= type.cost;
}

export function consumeOffer(state: GameState, offerIndex: number): void {
  const offer = state.shop.offers[offerIndex];
  if (!offer) return;
  const type = getPieceType(state.config, offer.pieceTypeId);
  state.player.gold -= type.cost;
  offer.consumed = true;
}

export function rerollShop(state: GameState): boolean {
  if (state.player.gold < state.config.economy.rerollCost) return false;
  state.player.gold -= state.config.economy.rerollCost;
  state.shop.offers = generateShopOffers(state.config, state.settings.loadoutPreset);
  return true;
}

export function applyCycleIncome(state: GameState): void {
  // 累進収入: 4g + 2 サイクルごと +1g(両陣営共通)
  //   cycle 2 で +0(=4g) / cycle 3 で +1(=5g) / cycle 5 で +2(=6g) / cycle 7 で +3(=7g)
  const baseIncome = state.config.economy.incomePerCycle;  // config では 4
  const cycleBonus = Math.floor((state.cycle - 1) / 2);
  const totalIncome = baseIncome + cycleBonus;

  const interest = Math.min(
    Math.floor(state.player.gold * state.config.economy.interestRate),
    state.config.economy.interestCap,
  );
  state.player.gold += totalIncome + interest;

  const aiInterest = Math.min(
    Math.floor(state.enemy.gold * state.config.economy.interestRate),
    state.config.economy.interestCap,
  );
  state.enemy.gold += totalIncome + aiInterest;
}

export function getOfferType(state: GameState, offerIndex: number): PieceTypeId | null {
  const offer = state.shop.offers[offerIndex];
  if (!offer) return null;
  return offer.pieceTypeId;
}

// 次サイクル開始時の予想収入(基本 + サイクルボーナス + 利息)
export function projectedNextIncome(state: GameState): number {
  // 次サイクルの番号
  const nextCycle = state.cycle + 1;
  const baseIncome = state.config.economy.incomePerCycle;
  const cycleBonus = Math.floor((nextCycle - 1) / 2);
  const interest = Math.min(
    Math.floor(state.player.gold * state.config.economy.interestRate),
    state.config.economy.interestCap,
  );
  return baseIncome + cycleBonus + interest;
}
